/**
 * Product variant selection functionality
 * Handles variant dropdowns and radio buttons, updates pricing, media, URLs,
 * and manages product availability states for variant changes
 */

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
  }

  /**
   * Handles variant selection change events
   * Updates all related product information and UI states
   */
  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.dispatchVariantChange();
    }
  }

  /**
   * Updates the selected options array from select elements
   */
  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  /**
   * Finds and sets the current variant based on selected options
   */
  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });
  }

  /**
   * Dispatches custom variant-change event for other components to listen to
   */
  dispatchVariantChange() {
    document.dispatchEvent(
      new CustomEvent(
        'variant-change',
        {
          bubbles: true,
          variant: this.currentVariant,
          detail: {
            variant: this.currentVariant
          }
        }
      )
    );
  }

  /**
   * Updates product media to show variant-specific images/videos
   */
  updateMedia() {
    if (!this.currentVariant || !this.currentVariant?.featured_media) return;
    const newMedia = document.querySelector(
      `[data-media-id="${this.dataset.section}-${this.currentVariant.featured_media.id}"]`
    );
    if (!newMedia) return;
    const parent = newMedia.parentElement;
    parent.prepend(newMedia);
    window.setTimeout(() => { parent.scroll(0, 0) });
  }

  /**
   * Updates browser URL with variant parameter without page reload
   */
  updateURL() {
    if (!this.currentVariant) return;
    window.history.replaceState({ }, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  /**
   * Updates hidden variant ID inputs in product forms
   */
  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /**
   * Fetches and renders updated product information (price, availability)
   * via AJAX when variant changes
   */
  renderProductInfo() {
    fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`)
      .then((response) => response.text())
      .then((responseText) => {
        const id = `price-${this.dataset.section}`;
        const html = new DOMParser().parseFromString(responseText, 'text/html')
        const destination = document.getElementById(id);
        const source = html.getElementById(id);

        if (source && destination) destination.innerHTML = source.innerHTML;

        document.getElementById(`price-${this.dataset.section}`)?.classList.remove('visibility-hidden');
        if (this.currentVariant) {
          this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
        }

        // Update the inventory quantity hidden input
        const inventoryQtyDestination = document.querySelector(`#product-form-${this.dataset.section}`).querySelector('input[name="inventory_quantity"]');
        const inventoryQtySource = html.querySelector(`#product-form-${this.dataset.section}`).querySelector('input[name="inventory_quantity"]');
        inventoryQtyDestination.value = inventoryQtySource.value;
      });
  }

  /**
   * Controls the state and text of the add-to-cart button
   * @param {boolean} disable - Whether to disable the button
   * @param {string} text - Button text to display
   * @param {boolean} modifyClass - Whether to modify CSS classes
   */
  toggleAddButton(disable = true, text, modifyClass = true) {
    const addButton = document.getElementById(`product-form-${this.dataset.section}`)?.querySelector('[name="add"]');

    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', true);
      if (text) addButton.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButton.textContent = window.variantStrings.addToCart;
    }

    if (!modifyClass) return;
  }

  /**
   * Sets the product form to unavailable state
   * Hides pricing and disables add to cart
   */
  setUnavailable() {
    const addButton = document.getElementById(`product-form-${this.dataset.section}`)?.querySelector('[name="add"]');
    if (!addButton) return;
    addButton.textContent = window.variantStrings.unavailable;
    document.getElementById(`price-${this.dataset.section}`)?.classList.add('visibility-hidden');
  }

  /**
   * Retrieves variant data from JSON script tag
   * @returns {Array|null} Array of variant objects or null
   */
  getVariantData() {
    const jsonDataElement = this.querySelector('[type="application/json"]');
    if (!jsonDataElement) {
      // console.warning('JSON variant data element not found');
      return null;
    }
    this.variantData = this.variantData || JSON.parse(jsonDataElement.textContent);
    return this.variantData;
  }
}

if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', VariantSelects);
}

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  /**
   * Updates selected options from radio button fieldsets
   * Overrides parent method for radio button specific logic
   */
  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }
}

if (!customElements.get('variant-radios')) {
  customElements.define('variant-radios', VariantRadios);
}

